import { z } from "zod";

// ============================================
// Virtual Showrooms — `showrooms/{supplierId}` Firestore documents
// ============================================
//
// A showroom doc describes the LAYOUT of a brand's Virtual Showroom page in
// the creator app (/home/showrooms/:slug). Brand identity (name, logo,
// website, description) stays on the existing `suppliers/{id}` doc; the
// showroom only references products, collections and boards by id and
// carries the images/copy that are specific to the showroom presentation.
//
// One schema, three writers: the seed script (packages/schemas/showrooms/),
// the future admin editor (apps/backend) and the future brand self-service
// API (ExpressApp) all validate through `ShowroomSchema` so a doc the creator
// cannot render never reaches Firestore. The creator parses on read too and
// degrades to "Showroom unavailable" on failure rather than crashing.

/** Directory chip categories (the "All Categories / Materials / Paints…" row). */
export const ShowroomCategoryEnum = z.enum([
  "materials",
  "paints",
  "hardware",
  "accessories",
  "furniture",
  "lighting",
]);
export type ShowroomCategory = z.infer<typeof ShowroomCategoryEnum>;

export const SHOWROOM_CATEGORY_LABELS: Record<ShowroomCategory, string> = {
  materials: "Materials",
  paints: "Paints",
  hardware: "Hardware",
  accessories: "Accessories",
  furniture: "Furniture",
  lighting: "Lighting",
};

/** Firestore document id: one non-empty path segment. */
const DocId = z
  .string()
  .min(1)
  .max(120)
  .refine((v) => !v.includes("/") && v !== "." && v !== "..", {
    message: "must be a single Firestore path segment",
  });
const ImageUrl = z.string().url().max(2000);
/** "#rrggbb"; also used by the admin editor's colour picker. */
export const HexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "expected #rrggbb");
/** List caps, shared with the admin editor's "Add" buttons. */
export const SHOWROOM_LIMITS = {
  sections: 12,
  items: 12,
  products: 24,
} as const;
const Title = z.string().min(1).max(120);

// ---- Sections -------------------------------------------------------------

/** A row of catalog product cards ("Behr 2027 Color Trends"). */
export const ProductRowSectionSchema = z
  .object({
    type: z.literal("productRow"),
    id: DocId,
    title: Title,
    productIds: z.array(DocId).min(1).max(SHOWROOM_LIMITS.products),
  })
  .strict();

/**
 * The fan-deck photo beside an animated paint-blob GIF. "View 3D Virtual
 * Samples" opens the brand's showroom board at /show/:boardId.
 */
export const VirtualSamplesSectionSchema = z
  .object({
    type: z.literal("virtualSamples"),
    id: DocId,
    title: Title.optional(),
    /** Fan-deck photo (left panel). */
    image: ImageUrl,
    /** Animated GIF of the paint blob (right panel), served unresized. */
    blobImage: ImageUrl,
    /**
     * Optional video sources for the same blob. The browser takes the first it
     * can play (VP9 WebM, else H.264 MP4); `blobImage` stays the fallback for
     * refused autoplay (Low Power Mode, data saver) and reduced motion.
     */
    blobVideo: z
      .object({ webm: ImageUrl.optional(), mp4: ImageUrl.optional() })
      .strict()
      .optional(),
    /** The paint the blob shows; its name captions the panel. */
    productId: DocId.optional(),
    /** `projects` doc the button opens at /show/:boardId. */
    boardId: DocId,
  })
  .strict();

/** "Explore by Palette": cards that open existing collections. */
export const CollectionRowItemSchema = z
  .object({
    collectionId: DocId,
    /** Display override; defaults to the collection's own name. */
    title: Title.optional(),
    /** Display override; defaults to the collection's cover/thumbnail. */
    coverImage: ImageUrl.optional(),
    /** Optional image cross-faded in on hover (the mockup's palette flat-lay). */
    hoverImage: ImageUrl.optional(),
  })
  .strict();

export const CollectionRowSectionSchema = z
  .object({
    type: z.literal("collectionRow"),
    id: DocId,
    title: Title,
    items: z.array(CollectionRowItemSchema).min(1).max(SHOWROOM_LIMITS.items),
  })
  .strict();

/** One slide of "Explore Colors in Curated Spaces". */
export const CuratedSpaceSlideSchema = z
  .object({
    id: DocId,
    /** The paint shown as a floating card over the room photo. */
    heroProductId: DocId,
    /** Optional animated paint blob (GIF) shown in that card instead of the product render. */
    heroImage: ImageUrl.optional(),
    /** Optional panel colour for this slide; defaults to the section background. */
    background: HexColor.optional(),
    roomImage: ImageUrl,
    boardImage: ImageUrl,
    /** "<lead> <text>" — lead renders bold ("Dark Crimson walls set…"). */
    caption: z
      .object({
        lead: z.string().max(60),
        text: z.string().max(300),
      })
      .strict(),
    /** `projects` doc the "Create Your Own" button opens at /show/:boardId. */
    boardId: DocId,
  })
  .strict();

export const CuratedSpacesSectionSchema = z
  .object({
    type: z.literal("curatedSpaces"),
    id: DocId,
    title: Title,
    /** Section background; the mockup uses the hero paint's deep tone. */
    background: HexColor,
    slides: z.array(CuratedSpaceSlideSchema).min(1).max(SHOWROOM_LIMITS.items),
  })
  .strict();

/**
 * The searchable brand product library: the embedded library browser with
 * the brand facet pinned. `brandFacetValue` is the LIBRARY facet value (the
 * lowercased `supplier` string the search MV exposes, e.g. "behr®"), not the
 * supplierID.
 */
export const ProductLibrarySectionSchema = z
  .object({
    type: z.literal("productLibrary"),
    id: DocId,
    title: Title,
    brandFacetValue: z.string().min(1).max(120),
    /**
     * The first screen, in order. Browse ranking re-enters a global shuffle on
     * every request, so without this the preview is a different dozen products
     * on every load. These ids also travel to /home/library behind "View all
     * products", so both surfaces open on the same row.
     */
    featuredProductIds: z
      .array(DocId)
      .min(1)
      .max(SHOWROOM_LIMITS.products)
      .optional(),
  })
  .strict();

export const ShowroomSectionSchema = z.discriminatedUnion("type", [
  ProductRowSectionSchema,
  VirtualSamplesSectionSchema,
  CollectionRowSectionSchema,
  CuratedSpacesSectionSchema,
  ProductLibrarySectionSchema,
]);
export type ShowroomSection = z.infer<typeof ShowroomSectionSchema>;
export type ShowroomSectionType = ShowroomSection["type"];

// ---- Links ----------------------------------------------------------------

/**
 * What the brand-level "Order Sample" button does. `swatchbox` opens the
 * Swatchbox widget modal on `productId` (Behr: 248, "BEHR Color System" —
 * the full colour picker) with nothing added to the cart; per-product
 * buttons use the widget too (apps/creator/src/integrations/swatchbox).
 * `url` opens an external page.
 */
export const OrderSampleLinkSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("swatchbox"),
      /** Swatchbox product id; without it the brand-level button scrolls to the product library. */
      productId: z.string().regex(/^\d+$/, "digits only").optional(),
    })
    .strict(),
  z.object({ kind: z.literal("url"), url: ImageUrl }).strict(),
]);
export type OrderSampleLink = z.infer<typeof OrderSampleLinkSchema>;

// ---- Document -------------------------------------------------------------

export const SHOWROOM_SCHEMA_VERSION = 1;

export const ShowroomSchema = z
  .object({
    /** `suppliers/{id}` this showroom belongs to; also the doc id. */
    supplierId: DocId,
    /** URL segment: /home/showrooms/:slug. */
    slug: z.string().regex(/^[a-z0-9-]+$/, "lowercase letters, digits, dashes"),
    /** Off → hidden from the directory and the brand page 404s. */
    enabled: z.boolean(),
    /** Directory sort order (ascending). */
    order: z.number().int(),
    category: ShowroomCategoryEnum,
    /** Breadcrumb/card name override ("BEHR"); defaults to supplier.name. */
    displayName: z.string().min(1).max(80).optional(),
    card: z
      .object({
        /** Flat tint of the card's left panel. */
        tint: HexColor,
        image: ImageUrl,
        /** Top-left logo; defaults to the supplier's logo, else the name as text. */
        logo: ImageUrl.optional(),
        /** Fallback when the live product count is unavailable. */
        productCount: z.number().int().nonnegative().optional(),
      })
      .strict(),
    hero: z
      .object({
        /** Still; also the poster (first frame) when `video` is set. */
        image: ImageUrl,
        /**
         * Heading override for the hero only ("Behr Paint Company"); else
         * `displayName`, which stays the short name the breadcrumb and the
         * directory card want.
         */
        name: z.string().min(1).max(80).optional(),
        /** Optional muted looping MP4 (H.264) played over the still on desktop. */
        video: ImageUrl.optional(),
        /** Logo tile override (e.g. icon only); defaults to the supplier's logo. */
        logo: ImageUrl.optional(),
        /** "SANTA ANA, CALIFORNIA" */
        location: z.string().max(80),
      })
      .strict(),
    /** Plain-text override; defaults to supplier.description (HTML). */
    description: z.string().max(2000).optional(),
    links: z
      .object({
        /** Defaults to supplier.URL when absent. */
        website: ImageUrl.optional(),
        orderSample: OrderSampleLinkSchema.optional(),
      })
      .strict(),
    sections: z
      .array(ShowroomSectionSchema)
      .max(SHOWROOM_LIMITS.sections)
      .superRefine((sections, ctx) => {
        const ids = new Map<string, number>();
        sections.forEach((section, index) => {
          const seen = ids.get(section.id);
          if (seen !== undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [index, "id"],
              message: `duplicate section id "${section.id}" (also at index ${seen})`,
            });
          } else {
            ids.set(section.id, index);
          }
        });
      }),
    version: z.literal(SHOWROOM_SCHEMA_VERSION),
    updatedAt: z.number(),
    /** uid or "seed" — who last wrote the doc. */
    updatedBy: z.string().min(1).max(200),
  })
  .strict();

export type Showroom = z.infer<typeof ShowroomSchema>;

/** Editor ⇄ preview tab: the preview posts READY; the editor answers DOC `{ type, doc }` on each change. */
export const SHOWROOM_PREVIEW_READY = "showroom-preview:ready";
export const SHOWROOM_PREVIEW_DOC = "showroom-preview:doc";
/** `showrooms/{supplierId}/previews/{token}`: the saved draft behind a share link. */
export const SHOWROOM_PREVIEWS_SUBCOLLECTION = "previews";
export const showroomPreviewPath = (supplierId: string) =>
  `/showroom-preview/${encodeURIComponent(supplierId)}`;
/** Share link: the saved draft at `showrooms/{supplierId}/previews/{token}`. */
export const showroomSharePath = (supplierId: string, token: string) =>
  `${showroomPreviewPath(supplierId)}/${encodeURIComponent(token)}`;

/** Every product id a showroom references (for bulk hydration). */
export const collectShowroomProductIds = (
  sections: ShowroomSection[],
): string[] => {
  const ids = new Set<string>();
  for (const section of sections) {
    switch (section.type) {
      case "productRow":
        section.productIds.forEach((id) => ids.add(id));
        break;
      case "curatedSpaces":
        section.slides.forEach((slide) => ids.add(slide.heroProductId));
        break;
      case "virtualSamples":
        if (section.productId) ids.add(section.productId);
        break;
      case "productLibrary":
        section.featuredProductIds?.forEach((id) => ids.add(id));
        break;
      default:
        break;
    }
  }
  return [...ids];
};

/** Every collection id a showroom references. */
export const collectShowroomCollectionIds = (
  sections: ShowroomSection[],
): string[] => {
  const ids = new Set<string>();
  for (const section of sections) {
    if (section.type === "collectionRow")
      section.items.forEach((item) => ids.add(item.collectionId));
  }
  return [...ids];
};

/** Every `projects` (board) id a showroom references. */
export const collectShowroomBoardIds = (
  sections: ShowroomSection[],
): string[] => {
  const ids = new Set<string>();
  for (const section of sections) {
    if (section.type === "curatedSpaces")
      section.slides.forEach((slide) => ids.add(slide.boardId));
    if (section.type === "virtualSamples") ids.add(section.boardId);
  }
  return [...ids];
};
